import React, { useState, useMemo, useEffect } from 'react';
import { AlertCircle, Calendar, Percent, DollarSign, ChevronRight, TrendingUp, TrendingDown, ChevronUp, ChevronDown, Pencil, Plus, Download } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import ResultsAddModal from '../components/ResultsAddModal';

function Results({ darkMode = false }) {
  const { axiosPost, fetchPostJson } = useApi();

  // Get tomorrow's date as default for end date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  const [startDate, setStartDate] = useState('2026-11-04');
  const [endDate, setEndDate] = useState(tomorrowStr);
  const [transactionFee, setTransactionFee] = useState(0.05);
  const [volumeFee, setVolumeFee] = useState(0.45);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [showComments, setShowComments] = useState(true);
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Accumulated PnL chart hover: y-value of the highlighted point
  const [hoverAccPnl, setHoverAccPnl] = useState(null);

  // Add Results Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [addFormData, setAddFormData] = useState({
    date: tomorrowStr,
    baseAction: 'Idle',
    basePrice: 0,
    volume: 0,
    isBaseActionInternal: false,
    bought: 0,
    sold: 0,
    totalVolume: 0,
    comment: ''
  });

  const handleShowResults = async () => {
    setLoading(true);
    setError('');
    setResults(null);

    try {
      const response = await axiosPost('/results', {
        start_date: startDate,
        end_date: endDate,
        transaction_fee: transactionFee,
        volume_fee: volumeFee
      });

      if (response.data.status === 'success') {
        setResults(response.data.results);
      } else {
        setError(response.data.message || 'Failed to get results');
      }
    } catch (err) {
      console.error('Results error:', err);
      setError(err.message || 'Failed to get results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportResults = async () => {
    setExporting(true);
    setError('');

    try {
      const response = await fetchPostJson('/results/export', {
        start_date: startDate,
        end_date: endDate,
        transaction_fee: transactionFee,
        volume_fee: volumeFee
      });

      if (!response.ok) {
        let message = 'Failed to export results';
        try {
          const err = await response.json();
          message = err.detail || err.message || message;
        } catch (_) { /* non-JSON error body */ }
        throw new Error(message);
      }

      const blob = await response.blob();

      // Derive filename from Content-Disposition, fall back to date range
      let filename = `results_${startDate}_${endDate}.xlsx`;
      const disposition = response.headers.get('Content-Disposition');
      const match = disposition && disposition.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export results error:', err);
      setError(err.message || 'Failed to export results. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Load results on mount
  useEffect(() => {
    handleShowResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatNumber = (num) => {
    return num?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00';
  };

  const formatInteger = (num) => {
    return Math.round(num || 0).toLocaleString('en-US');
  };

  const panelClass = darkMode
    ? 'bg-slate-900 border border-slate-800 shadow-xl shadow-slate-950/20'
    : 'bg-white shadow-sm';
  const secondaryPanelClass = darkMode
    ? 'bg-slate-950/70 border border-slate-800 shadow-lg shadow-slate-950/20'
    : 'bg-white shadow-sm';
  const inputClass = darkMode
    ? 'w-full px-3 py-2 border border-slate-700 bg-slate-950 text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
    : 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort the daily data and pre-calculate accumulated values
  const sortedDaily = useMemo(() => {
    if (!results?.daily) return [];

    // First, sort by date ASC to calculate accumulated values correctly
    const dateOrdered = [...results.daily].sort((a, b) =>
      a.date < b.date ? -1 : a.date > b.date ? 1 : 0
    );

    // Calculate accumulated values in chronological order
    let accPnl = 0;
    let accPnlMinusFee = 0;
    const withAccumulated = dateOrdered.map(day => {
      accPnl += day.pnl || 0;
      accPnlMinusFee += day.pnl_minus_fee || 0;
      const pnlPerMwh = day.total_volume > 0 ? day.pnl_minus_fee / day.total_volume : 0;
      return {
        ...day,
        acc_pnl: accPnl,
        acc_pnl_minus_fee: accPnlMinusFee,
        pnl_per_mwh: pnlPerMwh
      };
    });

    // Now sort by the selected column/direction for display
    const sorted = withAccumulated.sort((a, b) => {
      let aVal, bVal;

      if (sortColumn === 'date') {
        aVal = a.date;
        bVal = b.date;
      } else {
        aVal = a[sortColumn] || 0;
        bVal = b[sortColumn] || 0;
      }

      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    return sorted;
  }, [results?.daily, sortColumn, sortDirection]);

  // Chart data - always sorted by date ascending
  const chartData = useMemo(() => {
    if (!results?.daily) return [];

    // Sort by date ascending and calculate accumulated values
    const dateOrdered = [...results.daily].sort((a, b) =>
      a.date < b.date ? -1 : a.date > b.date ? 1 : 0
    );

    let accPnlMinusFee = 0;
    return dateOrdered.map(day => {
      accPnlMinusFee += day.pnl_minus_fee || 0;
      return {
        date: day.date,
        acc_pnl_minus_fee: Math.round(accPnlMinusFee * 100) / 100
      };
    });
  }, [results?.daily]);

  // Render sortable header
  const SortableHeader = ({ column, label, align = 'right' }) => (
    <th
      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider cursor-pointer select-none ${darkMode ? 'text-slate-400 hover:bg-slate-800/80' : 'text-gray-600 hover:bg-gray-100'} ${align === 'left' ? 'text-left' : 'text-right'}`}
      onClick={() => handleSort(column)}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        <span>{label}</span>
        <div className="flex flex-col">
          <ChevronUp className={`w-3 h-3 -mb-1 ${sortColumn === column && sortDirection === 'asc' ? 'text-blue-500' : darkMode ? 'text-slate-600' : 'text-gray-300'}`} />
          <ChevronDown className={`w-3 h-3 -mt-1 ${sortColumn === column && sortDirection === 'desc' ? 'text-blue-500' : darkMode ? 'text-slate-600' : 'text-gray-300'}`} />
        </div>
      </div>
    </th>
  );

  const handleOpenAddModal = () => {
    setAddFormData({
      date: tomorrowStr,
      baseAction: 'Idle',
      basePrice: 0,
      volume: 0,
      isBaseActionInternal: false,
      bought: 0,
      sold: 0,
      totalVolume: 0,
      comment: ''
    });
    setAddError('');
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setAddError('');
    setEditMode(false);
    setEditingDate(null);
  };

  const handleEditRow = async (dayData) => {
    setEditLoading(true);
    setAddError('');

    try {
      const response = await axiosPost('/get-result', { date: dayData.date });

      if (response.data.status === 'success') {
        const data = response.data.data;

        // Determine base action from base_volume sign
        let baseAction = 'Idle';
        let volume = 0;
        if (data.base_volume > 0) {
          baseAction = 'Buy';
          volume = data.base_volume;
        } else if (data.base_volume < 0) {
          baseAction = 'Sell';
          volume = Math.abs(data.base_volume);
        }

        // Override with stored base_action if available
        if (data.base_action && data.base_action.toLowerCase() !== 'idle') {
          baseAction = data.base_action.charAt(0).toUpperCase() + data.base_action.slice(1).toLowerCase();
        }

        setAddFormData({
          date: data.date,
          baseAction: baseAction,
          basePrice: data.base_price || 0,
          volume: volume,
          isBaseActionInternal: data.is_base_action_internal || false,
          bought: data.bought || 0,
          sold: data.sold || 0,
          totalVolume: data.total_volume || 0,
          comment: data.comment || ''
        });

        setEditMode(true);
        setEditingDate(data.date);
        setShowAddModal(true);
      } else {
        setAddError(response.data.message || 'Failed to fetch result data');
      }
    } catch (err) {
      console.error('Edit row error:', err);
      setAddError(err.message || 'Failed to fetch result data. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleAddFormChange = (field, value) => {
    setAddFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Reset basePrice, volume, and isBaseActionInternal when switching to Idle
      if (field === 'baseAction' && value === 'Idle') {
        updated.basePrice = 0;
        updated.volume = 0;
        updated.isBaseActionInternal = false;
      }
      return updated;
    });
  };

  const handleAddResult = async (overrideData = null) => {
    setAddLoading(true);
    setAddError('');

    const data = overrideData || addFormData;

    try {
      const response = await axiosPost('/add-result', {
        date: data.date,
        base_action: data.baseAction,
        base_price: data.baseAction !== 'Idle' ? data.basePrice : 0,
        volume: data.baseAction !== 'Idle' ? data.volume : 0,
        is_base_action_internal: data.baseAction !== 'Idle' ? data.isBaseActionInternal : false,
        bought: data.bought,
        sold: data.sold,
        total_volume: data.totalVolume,
        comment: data.comment,
        volume_fee_rate: volumeFee,
        transaction_fee_rate: transactionFee
      });

      if (response.data.status === 'success') {
        setShowAddModal(false);
        // Refresh results if they were already loaded
        if (results) {
          handleShowResults();
        }
      } else {
        setAddError(response.data.message || 'Failed to add result');
      }
    } catch (err) {
      console.error('Add result error:', err);
      setAddError(err.message || 'Failed to add result. Please try again.');
    } finally {
      setAddLoading(false);
    }
  };

  const handleNoAction = () => {
    const noActionData = {
      date: addFormData.date,
      baseAction: 'Idle',
      basePrice: 0,
      volume: 0,
      bought: 0,
      sold: 0,
      totalVolume: 0,
      comment: ''
    };
    handleAddResult(noActionData);
  };

  return (
    <div className={`min-h-screen p-8 ${darkMode ? 'bg-transparent' : 'bg-gray-50'}`}>
      <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className={`text-4xl font-bold mb-2 ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>Results</h1>
          <p className={darkMode ? 'text-slate-400' : 'text-gray-600'}>View trading results with fee calculations</p>
        </div>

        {/* Parameters Form */}
        <div className={`${panelClass} rounded-xl p-6 mb-6`}>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Start Date */}
            <div>
              <label className={`flex items-center space-x-2 text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                <Calendar size={16} />
                <span>Start Date</span>
              </label>
              <input
                type="date"
                value={startDate}
                min="2026-01-01"
                onChange={(e) => setStartDate(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* End Date */}
            <div>
              <label className={`flex items-center space-x-2 text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                <Calendar size={16} />
                <span>End Date</span>
              </label>
              <input
                type="date"
                value={endDate}
                min="2026-01-01"
                onChange={(e) => setEndDate(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Transaction Fee */}
            <div>
              <label className={`flex items-center space-x-2 text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                <Percent size={16} />
                <span>Transaction Fee (%)</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={transactionFee}
                onChange={(e) => setTransactionFee(parseFloat(e.target.value) || 0)}
                className={inputClass}
              />
            </div>

            {/* Volume Fee */}
            <div>
              <label className={`flex items-center space-x-2 text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                <DollarSign size={16} />
                <span>Volume Fee (PLN)</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={volumeFee}
                onChange={(e) => setVolumeFee(parseFloat(e.target.value) || 0)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Buttons Row */}
          <div className="mt-6 flex gap-4">
            <button
              onClick={handleOpenAddModal}
              className="flex-1 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Add Result</span>
            </button>
            <button
              onClick={handleShowResults}
              disabled={loading}
              className="flex-1 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Loading Results...</span>
                </>
              ) : (
                <>
                  <ChevronRight className="w-5 h-5" />
                  <span>Show Results</span>
                </>
              )}
            </button>
            <button
              onClick={handleExportResults}
              disabled={exporting}
              className="flex-1 py-3 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            >
              {exporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  <span>Export Results</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 rounded-xl p-4 flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Results Display */}
        {results && results.summary && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total PnL */}
              <div className={`${secondaryPanelClass} rounded-xl p-6`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total PnL</p>
                    <p className={`text-2xl font-bold ${results.summary.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatNumber(results.summary.total_pnl)} PLN
                    </p>
                  </div>
                  {results.summary.total_pnl >= 0 ? (
                    <TrendingUp className="w-8 h-8 text-green-500" />
                  ) : (
                    <TrendingDown className="w-8 h-8 text-red-500" />
                  )}
                </div>
              </div>

              {/* Total PnL Minus Fees */}
              <div className={`${secondaryPanelClass} rounded-xl p-6`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">PnL After Fees</p>
                    <p className={`text-2xl font-bold ${results.summary.total_pnl_minus_fee >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatNumber(results.summary.total_pnl_minus_fee)} PLN
                    </p>
                  </div>
                  {results.summary.total_pnl_minus_fee >= 0 ? (
                    <TrendingUp className="w-8 h-8 text-green-500" />
                  ) : (
                    <TrendingDown className="w-8 h-8 text-red-500" />
                  )}
                </div>
              </div>

              {/* Total Volume */}
              <div className={`${secondaryPanelClass} rounded-xl p-6`}>
                <div>
                  <p className="text-sm text-gray-500">Total Volume</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {formatInteger(results.summary.total_volume)} MWh
                  </p>
                </div>
              </div>

              {/* Number of Days */}
              <div className={`${secondaryPanelClass} rounded-xl p-6`}>
                <div>
                  <p className="text-sm text-gray-500">Trading Days</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {results.summary.num_days}
                  </p>
                </div>
              </div>
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className={`${secondaryPanelClass} rounded-xl p-4`}>
                <p className="text-sm text-gray-500">Total Volume Fee</p>
                <p className="text-lg font-semibold text-gray-800">{formatNumber(results.summary.total_volume_fee)} PLN</p>
              </div>
              <div className={`${secondaryPanelClass} rounded-xl p-4`}>
                <p className="text-sm text-gray-500">Total Transaction Fee</p>
                <p className="text-lg font-semibold text-gray-800">{formatNumber(results.summary.total_transaction_fee)} PLN</p>
              </div>
              <div className={`${secondaryPanelClass} rounded-xl p-4`}>
                <p className="text-sm text-gray-500">Avg Daily Volume</p>
                <p className="text-lg font-semibold text-gray-800">{formatInteger(results.summary.avg_total_volume)} MWh</p>
              </div>
              <div className={`${secondaryPanelClass} rounded-xl p-4`}>
                <p className="text-sm text-gray-500">Avg Base Volume</p>
                <p className="text-lg font-semibold text-gray-800">{formatInteger(results.summary.avg_base_volume)} MW</p>
              </div>
              <div className={`${secondaryPanelClass} rounded-xl p-4`}>
                <p className="text-sm text-gray-500">Avg Profit per MWh</p>
                <p className={`text-lg font-semibold ${results.summary.total_volume > 0 ? (results.summary.total_pnl / results.summary.total_volume >= 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-800'}`}>
                  {results.summary.total_volume > 0 ? formatNumber(results.summary.total_pnl / results.summary.total_volume) : '0.00'} PLN
                </p>
              </div>
            </div>

            {/* Daily Breakdown Table */}
            {results.daily && results.daily.length > 0 && (
              <div className={`${panelClass} rounded-xl overflow-hidden`}>
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className={`text-lg font-semibold ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>Daily Results</h2>
                  {/* Comments Toggle */}
                  <div className="flex items-center space-x-3">
                    <span className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-gray-600'}>Show Comments</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showComments}
                        onChange={(e) => setShowComments(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 border border-gray-400 shadow-sm peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:border-blue-500 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border after:border-gray-400 after:shadow-sm after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={darkMode ? 'bg-slate-950/90 border-b border-slate-800' : 'bg-gray-50 border-b border-gray-200'}>
                        <SortableHeader column="date" label="Date" align="left" />
                        <SortableHeader column="pnl" label="PnL" />
                        <SortableHeader column="pnl_minus_fee" label="PnL After Fees" />
                        <SortableHeader column="pnl_per_mwh" label="PNL/MWH" />
                        <SortableHeader column="total_volume" label="Total Volume" />
                        <SortableHeader column="base_volume" label="Base Volume" />
                        <SortableHeader column="volume_fee" label="Volume Fee" />
                        <SortableHeader column="transaction_fee" label="Transaction Fee" />
                        <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>Acc. PnL</th>
                        <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>Acc. PnL After Fees</th>
                        <th className={`px-3 py-3 text-xs font-medium uppercase tracking-wider w-10 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDaily.map((day, index) => (
                        <React.Fragment key={index}>
                          <tr className={`${day.pnl_minus_fee > 0 ? 'bg-green-50 hover:bg-green-100' : day.pnl_minus_fee < 0 ? 'bg-red-50 hover:bg-red-100' : 'bg-gray-50 hover:bg-gray-100'} ${showComments && day.comment ? '' : 'border-b border-gray-100'}`}>
                            <td className="px-6 py-3 text-sm text-gray-900">{day.date}</td>
                            <td className={`px-6 py-3 text-sm text-right ${day.pnl > 0 ? 'text-green-600' : day.pnl < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                              {formatNumber(day.pnl)}
                            </td>
                            <td className={`px-6 py-3 text-sm text-right font-medium ${day.pnl_minus_fee > 0 ? 'text-green-600' : day.pnl_minus_fee < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                              {formatNumber(day.pnl_minus_fee)}
                            </td>
                            <td className={`px-6 py-3 text-sm text-right ${day.pnl_per_mwh > 0 ? 'text-green-600' : day.pnl_per_mwh < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                              {day.total_volume > 0 ? formatNumber(day.pnl_per_mwh) : '-'}
                            </td>
                            <td className="px-6 py-3 text-sm text-gray-900 text-right">{formatInteger(day.total_volume)}</td>
                            <td className="px-6 py-3 text-sm text-gray-900 text-right">{formatInteger(day.base_volume)}</td>
                            <td className="px-6 py-3 text-sm text-gray-900 text-right">{formatNumber(day.volume_fee)}</td>
                            <td className="px-6 py-3 text-sm text-gray-900 text-right">{formatNumber(day.transaction_fee)}</td>
                            <td className={`px-6 py-3 text-sm text-right ${day.acc_pnl > 0 ? 'text-green-600' : day.acc_pnl < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                              {formatNumber(day.acc_pnl)}
                            </td>
                            <td className={`px-6 py-3 text-sm text-right font-medium ${day.acc_pnl_minus_fee > 0 ? 'text-green-600' : day.acc_pnl_minus_fee < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                              {formatNumber(day.acc_pnl_minus_fee)}
                            </td>
                            <td className="px-3 py-3 text-sm">
                              <button
                                onClick={() => handleEditRow(day)}
                                disabled={editLoading}
                                className={`p-1 rounded transition-colors disabled:opacity-50 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`}
                                title="Edit row"
                              >
                                <Pencil className={`w-4 h-4 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`} />
                              </button>
                            </td>
                          </tr>
                          {showComments && day.comment && (
                            <tr className={darkMode ? 'bg-slate-950/70 border-b border-slate-800' : 'bg-white border-b border-gray-200'}>
                              <td colSpan={11} className={`px-6 py-2 text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                {day.comment}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Accumulated PnL Chart */}
            {results?.daily && chartData.length > 0 && (
              <div className={`${panelClass} rounded-xl p-6 mt-6`}>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>Accumulated PnL After Fees</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={chartData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      onMouseMove={(state) => {
                        let value = state?.activePayload?.[0]?.value;
                        if ((value === undefined || value === null) && state?.activeLabel != null) {
                          value = chartData.find((d) => d.date === state.activeLabel)?.acc_pnl_minus_fee;
                        }
                        setHoverAccPnl(value === undefined || value === null ? null : value);
                      }}
                      onMouseLeave={() => setHoverAccPnl(null)}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#334155' : '#e5e7eb'} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: darkMode ? '#cbd5e1' : '#475569' }}
                        tickFormatter={(value) => value.slice(5)} // Show MM-DD
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: darkMode ? '#cbd5e1' : '#475569' }}
                        tickFormatter={(value) => `${value.toLocaleString()}`}
                      />
                      <Tooltip
                        formatter={(value) => [`${value.toLocaleString()} PLN`, 'Acc PnL After Fees']}
                        labelFormatter={(label) => `Date: ${label}`}
                        contentStyle={darkMode ? { backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '12px', color: '#e5e7eb' } : undefined}
                      />
                      <ReferenceLine y={0} stroke={darkMode ? '#475569' : '#9ca3af'} strokeDasharray="3 3" />
                      <ReferenceLine
                        y={hoverAccPnl ?? 0}
                        stroke={hoverAccPnl !== null ? '#3b82f6' : 'transparent'}
                        strokeDasharray="4 4"
                        strokeWidth={1.5}
                        ifOverflow="extendDomain"
                      />
                      <Line
                        type="linear"
                        dataKey="acc_pnl_minus_fee"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ResultsAddModal
        darkMode={darkMode}
        show={showAddModal}
        editMode={editMode}
        editingDate={editingDate}
        formData={addFormData}
        error={addError}
        loading={addLoading}
        onClose={handleCloseAddModal}
        onChange={handleAddFormChange}
        onSubmit={() => handleAddResult()}
        onNoAction={!editMode ? handleNoAction : null}
      />
    </div>
  );
}

export default Results;
